import { useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Cell, Pie, PieChart } from "recharts";
import {
  AlertTriangle, BarChart3, CalendarCheck, CalendarClock, CalendarRange, ChevronRight, CircleDollarSign, FileUp, GanttChartSquare,
  Home, Info, Landmark, Lightbulb, MapPin, Plus, RefreshCcw, Settings, ShoppingCart, TrendingUp, Wallet,
  ShieldAlert, ClipboardList, Activity, CalendarDays, Receipt, FileText,
} from "lucide-react";
import { usePermissions } from "@/lib/permissions";
import { projectPageModule } from "@/components/layout/navConfig";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/format";

const n = (v) => Number(v) || 0;
const clamp = (v) => Math.max(0, Math.min(100, v));
const pc = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(0)}%`);

function inr(v) {
  const x = n(v);
  const a = Math.abs(x);
  const s = x < 0 ? "-" : "";
  if (a >= 1e7) return `${s}₹${(a / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${s}₹${(a / 1e5).toFixed(2)} L`;
  return `${s}₹${a.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}
function ago(d) {
  if (!d) return "";
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

const BRAND = "#910029";

function Card({ title, icon: Icon, link, linkLabel = "View All", children, className, testId }) {
  return (
    <section className={cn("min-w-0 rounded-xl border border-border bg-card p-4 shadow-sm", className)} data-testid={testId}>
      <header className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
          {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}{title}
        </h2>
        {link && <Link to={link} className="flex items-center gap-0.5 text-xs font-medium hover:underline" style={{ color: BRAND }}>{linkLabel}<ChevronRight className="h-3 w-3" /></Link>}
      </header>
      {children}
    </section>
  );
}

function IconDot({ icon: Icon, bg, fg, size = "h-9 w-9" }) {
  return <span className={cn("flex shrink-0 items-center justify-center rounded-full", size)} style={{ background: bg, color: fg }}><Icon className="h-4 w-4" /></span>;
}

/** Decorative construction scene (drawn, no photo asset). */
function Scene() {
  return (
    <svg aria-hidden="true" viewBox="0 0 700 150" preserveAspectRatio="xMaxYMid slice" className="absolute inset-0 h-full w-full">
      <defs>
        <linearGradient id="p360sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1B2A4A" /><stop offset=".55" stopColor="#6B6F94" /><stop offset="1" stopColor="#E7A672" />
        </linearGradient>
        <linearGradient id="p360fade" x1="0" x2="1"><stop offset="0" stopColor="#0E1A33" stopOpacity="1" /><stop offset=".5" stopColor="#0E1A33" stopOpacity=".55" /><stop offset="1" stopColor="#0E1A33" stopOpacity="0" /></linearGradient>
      </defs>
      <rect width="700" height="150" fill="url(#p360sky)" />
      <g fill="#33405F" opacity=".7"><rect x="330" y="85" width="18" height="65" /><rect x="352" y="70" width="14" height="80" /><rect x="372" y="95" width="22" height="55" /></g>
      <g>
        <rect x="430" y="48" width="190" height="102" fill="#8B8F9C" />
        {[0, 1, 2, 3, 4, 5].map((i) => <rect key={`f${i}`} x="430" y={48 + i * 17} width="190" height="3" fill="#C9CBD2" />)}
        {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((i) => <rect key={`c${i}`} x={432 + i * 23} y="48" width="4" height="102" fill="#C9CBD2" />)}
        <rect x="430" y="48" width="190" height="102" fill="#F2A65A" opacity=".18" />
      </g>
      <g stroke="#E0A33A" strokeWidth="3" fill="none">
        <line x1="560" y1="150" x2="560" y2="6" /><line x1="470" y1="12" x2="690" y2="12" /><line x1="560" y1="6" x2="470" y2="12" />
        <line x1="650" y1="12" x2="650" y2="62" strokeWidth="1.5" />
        <path d="M552 150 L560 30 L568 150 M552 110 L568 110 M554 80 L566 80 M556 50 L564 50" strokeWidth="1.5" />
      </g>
      <rect width="700" height="150" fill="url(#p360fade)" />
    </svg>
  );
}

const TABS = [
  { id: "overview", label: "Overview", icon: Home, suffix: "360" },
  { id: "daily", label: "Daily Work", icon: CalendarCheck, suffix: "daily-reports" },
  { id: "planning", label: "Planning", icon: CalendarRange, suffix: "schedule" },
  { id: "finance", label: "Finance", icon: CircleDollarSign, suffix: "finance" },
  { id: "risks", label: "Risks & Issues", icon: AlertTriangle, suffix: "issues" },
  { id: "settings", label: "Settings", icon: Settings, suffix: "settings" },
];

const STATUS_TONE = { APPROVED: "bg-emerald-100 text-emerald-700", SUBMITTED: "bg-sky-100 text-sky-700", DRAFT: "bg-slate-100 text-slate-600", REJECTED: "bg-rose-100 text-rose-700" };
const Pill = ({ children, cls }) => <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", cls || "bg-slate-100 text-slate-600")}>{children}</span>;
const Empty = ({ children }) => <p className="py-6 text-center text-sm text-muted-foreground">{children}</p>;

function ProgressRow({ label, actual, planned }) {
  const a = clamp(n(actual)), p = clamp(n(planned));
  const behind = n(actual) < n(planned);
  return (
    <li>
      <div className="mb-1 flex items-center justify-between text-[13px]"><span className="font-medium text-foreground">{label}</span>
        <span className="text-muted-foreground"><b className={behind ? "text-rose-600" : "text-emerald-600"}>{pc(actual)}</b> of planned {pc(planned)}</span></div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-muted">
        <div className="absolute inset-y-0 left-0 rounded-full bg-[#BFD0F5]" style={{ width: `${p}%` }} />
        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${a}%`, background: BRAND }} />
      </div>
    </li>
  );
}

function Stat({ label, value, tone }) {
  return <div className="rounded-lg border border-border bg-background p-3"><p className="text-[11px] text-muted-foreground">{label}</p><p className={cn("mt-1 text-xl font-semibold text-foreground", tone)}>{value}</p></div>;
}

function TabPanel({ tab, ctx }) {
  const { base, data, project, projectRole, progress, tiles, health, gap, budget, spent, committed, spentPct } = ctx;
  const { header = {}, budget_vs_actual = [], recent_cost_entries = [], latest_daily_reports = [], open_issues = [], open_delays = [], open_alerts = [], procurement_overdue = [], upcoming_milestones = [] } = data;
  const open = (to, label) => <Link to={to} className="flex items-center gap-0.5 text-xs font-medium hover:underline" style={{ color: BRAND }}>{label}<ChevronRight className="h-3 w-3" /></Link>;

  if (tab === "daily") {
    return (
      <div className="space-y-4" data-testid="project-360-panel-daily">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Construction progress" value={pc(progress.construction_actual_pct)} />
          <Stat label="Planned to date" value={pc(progress.construction_planned_pct)} />
          <Stat label="Recent daily reports" value={latest_daily_reports.length} />
          <Stat label="Open delays" value={open_delays.length} tone={open_delays.length ? "text-amber-600" : ""} />
        </div>
        <Card title="Daily Work progress" link={`${base}/construction`} linkLabel="Update progress">
          <ul className="space-y-4">
            <ProgressRow label="Construction" actual={progress.construction_actual_pct} planned={progress.construction_planned_pct} />
            <ProgressRow label="Procurement" actual={progress.procurement_actual_pct} planned={progress.procurement_planned_pct} />
            <ProgressRow label="Engineering" actual={progress.engineering_actual_pct} planned={progress.engineering_planned_pct} />
          </ul>
        </Card>
        <Card title="Latest Daily Reports" icon={FileText} link={`${base}/daily-reports`} linkLabel="Open Daily Work">
          {latest_daily_reports.length === 0 ? <Empty>No reports yet</Empty> : (
            <ul className="divide-y divide-border">
              {latest_daily_reports.map((r) => (
                <li key={r.id || r.number} className="flex items-center justify-between gap-3 py-2.5 text-[13px]">
                  <Link to={`${base}/daily-reports`} className="font-medium text-foreground hover:underline">{r.number}</Link>
                  <span className="text-muted-foreground">{fmtDate(r.report_date)}</span>
                  <Pill cls={STATUS_TONE[String(r.status).toUpperCase()]}>{r.status}</Pill>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    );
  }

  if (tab === "planning") {
    return (
      <div className="space-y-4" data-testid="project-360-panel-planning">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Overall progress" value={pc(progress.overall_actual_pct)} />
          <Stat label="Planned" value={pc(progress.overall_planned_pct)} />
          <Stat label="Variance" value={`${gap > 0 ? "+" : ""}${gap.toFixed(1)} pts`} tone={gap < 0 ? "text-rose-600" : "text-emerald-600"} />
          <Stat label="Schedule health" value={health.t} tone="" />
        </div>
        <Card title="Planned vs Actual" link={`${base}/plan-vs-actual`} linkLabel="Open Plan vs Actual">
          <ul className="space-y-4">
            <ProgressRow label="Overall" actual={progress.overall_actual_pct} planned={progress.overall_planned_pct} />
            <ProgressRow label="Engineering" actual={progress.engineering_actual_pct} planned={progress.engineering_planned_pct} />
            <ProgressRow label="Procurement" actual={progress.procurement_actual_pct} planned={progress.procurement_planned_pct} />
            <ProgressRow label="Construction" actual={progress.construction_actual_pct} planned={progress.construction_planned_pct} />
          </ul>
        </Card>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card title="Upcoming Milestones" icon={CalendarDays} link={`${base}/schedule`} linkLabel="Open Schedule">
            {upcoming_milestones.length === 0 ? <Empty>No upcoming milestones</Empty> : (
              <ul className="space-y-3">
                {upcoming_milestones.map((m, i) => {
                  const p = m.progress_pct ?? m.percent_complete ?? m.actual_pct;
                  return (
                    <li key={m.id || i}>
                      <div className="flex justify-between gap-2 text-[13px]"><span className="font-medium text-foreground">{m.name}</span><span className="text-muted-foreground">{m.start_date ? `${fmtDate(m.start_date)} – ` : "Due "}{fmtDate(m.end_date)}</span></div>
                      {p !== undefined && p !== null && <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${clamp(n(p))}%`, background: BRAND }} /></div>}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
          <Card title="Open Delays" icon={CalendarClock} link={`${base}/delays`} linkLabel="Open Delays">
            {open_delays.length === 0 ? <Empty>No open delays</Empty> : (
              <ul className="divide-y divide-border">{open_delays.map((d, i) => <li key={d.id || i} className="flex items-center justify-between gap-2 py-2.5 text-[13px]"><span className="text-foreground">{d.category} — {d.reason}</span><Pill cls="bg-amber-100 text-amber-700">{d.impact_days}d</Pill></li>)}</ul>
            )}
          </Card>
        </div>
      </div>
    );
  }

  if (tab === "finance") {
    return (
      <div className="space-y-4" data-testid="project-360-panel-finance">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Total budget" value={inr(budget)} />
          <Stat label="Actual cost" value={`${inr(spent)} · ${spentPct.toFixed(0)}%`} />
          <Stat label="Committed" value={inr(committed)} />
          <Stat label="Variance" value={inr(tiles.variance)} tone={n(tiles.variance) < 0 ? "text-rose-600" : "text-emerald-600"} />
          <Stat label="Estimate to complete" value={inr(tiles.estimate_to_complete)} />
          <Stat label="Forecast at completion" value={inr(tiles.forecast_at_completion)} />
          {progress.certified_pct !== null && progress.certified_pct !== undefined && <Stat label="Certified (RA billed)" value={pc(progress.certified_pct)} />}
        </div>
        <Card title="Budget vs Actual by WBS" link={`${base}/finance`} linkLabel="Open Finance">
          {budget_vs_actual.length === 0 ? <Empty>No budget rows yet</Empty> : (
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-[13px]">
                <thead><tr className="border-b border-border text-left text-xs text-muted-foreground"><th className="p-2">Code</th><th className="p-2">Name</th><th className="p-2 text-right">Budget</th><th className="p-2 text-right">Actual</th><th className="p-2 text-right">Variance</th></tr></thead>
                <tbody>{budget_vs_actual.slice(0, 15).map((r, i) => (
                  <tr key={r.wbs_item_id || i} className="border-b border-border/50">
                    <td className="p-2 font-mono text-xs text-muted-foreground">{r.code || "—"}</td><td className="p-2 text-foreground">{r.name || "—"}</td>
                    <td className="p-2 text-right">{inr(r.budget)}</td><td className="p-2 text-right">{inr(r.actual)}</td>
                    <td className={cn("p-2 text-right font-semibold", n(r.variance) < 0 ? "text-rose-600" : "text-emerald-600")}>{inr(r.variance)}</td>
                  </tr>))}</tbody>
              </table>
            </div>
          )}
        </Card>
        <Card title="Recent Cost Entries" icon={Receipt} link={`${base}/cost-entries`} linkLabel="Open Cost Entries">
          {recent_cost_entries.length === 0 ? <Empty>No cost entries yet</Empty> : (
            <ul className="divide-y divide-border">{recent_cost_entries.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-3 py-2.5 text-[13px]"><span className="text-muted-foreground">{fmtDate(e.entry_date)} · {e.category} · {e.description || "—"}</span><b className="text-foreground">{inr(e.amount)}</b></li>
            ))}</ul>
          )}
        </Card>
      </div>
    );
  }

  if (tab === "risks") {
    const list = (title, icon, items, to, label, render, empty) => (
      <Card title={title} icon={icon} link={to} linkLabel={label}>
        {items.length === 0 ? <Empty>{empty}</Empty> : <ul className="divide-y divide-border">{items.map((it, i) => <li key={it.id || i} className="py-2.5 text-[13px] text-foreground">{render(it)}</li>)}</ul>}
      </Card>
    );
    return (
      <div className="space-y-4" data-testid="project-360-panel-risks">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Open issues" value={open_issues.length} />
          <Stat label="Open delays" value={open_delays.length} />
          <Stat label="Open alerts" value={open_alerts.length} tone={open_alerts.length ? "text-rose-600" : ""} />
          <Stat label="Procurement overdue" value={procurement_overdue.length} />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {list("Open Issues", AlertTriangle, open_issues, `${base}/issues`, "Open Issues", (i) => <>{i.description} <Pill cls="bg-amber-100 text-amber-700">{i.priority}</Pill></>, "No open issues")}
          {list("Open Alerts", ShieldAlert, open_alerts, `${base}/alerts`, "Open Alerts", (a) => <>{a.title} <Pill cls={String(a.severity).toUpperCase() === "CRITICAL" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}>{a.severity}</Pill></>, "No open alerts")}
          {list("Open Delays", CalendarClock, open_delays, `${base}/delays`, "Open Delays", (d) => <>{d.category} — {d.reason} <Pill cls="bg-amber-100 text-amber-700">{d.impact_days}d</Pill></>, "No open delays")}
          {list("Procurement Overdue", ShoppingCart, procurement_overdue, `${base}/alerts`, "Open Alerts", (a) => <>{a.title}</>, "Nothing overdue")}
        </div>
      </div>
    );
  }

  /* settings */
  const rows = [
    ["Project", project?.name || header.name], ["Code", project?.code || header.code], ["Company", header.company_name], ["Location", project?.location || header.location],
    ["Status", project?.status], ["Start", fmtDate(project?.start_date || header.start_date)], ["Target end", fmtDate(project?.target_end_date || header.target_end_date)],
    ["Contract value", project?.contract_value ? inr(project.contract_value) : null], ["My role", projectRole ? String(projectRole).replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase()) : null],
  ].filter(([, v]) => v && v !== "—");
  return (
    <div className="space-y-4" data-testid="project-360-panel-settings">
      <Card title="Project details" icon={Settings} link={`${base}/settings`} linkLabel="Open project settings">
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
          {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-2 text-[13px]"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-foreground">{v}</dd></div>)}
        </dl>
      </Card>
    </div>
  );
}

export default function Project360Overview({ data, project, projectId, projectRole, onRefresh }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { canView } = usePermissions();
  const base = `/projects/${projectId}`;
  const { header = {}, tiles = {}, progress = {}, recent_cost_entries = [], latest_daily_reports = [], open_issues = [], open_delays = [], open_alerts = [], procurement_overdue = [], upcoming_milestones = [] } = data;

  const overall = clamp(n(progress.overall_actual_pct));
  const planned = clamp(n(progress.overall_planned_pct));
  const gap = n(progress.overall_actual_pct) - n(progress.overall_planned_pct);
  const closed = project?.status === "COMPLETED" || project?.status === "CLOSED";
  const health = closed ? { t: "Completed", c: "#16A34A" } : gap >= -2 ? { t: "On Track", c: "#16A34A" } : gap >= -10 ? { t: "At Risk", c: "#D97706" } : { t: "Delayed", c: "#DC2626" };

  const budget = n(tiles.total_budget), spent = n(tiles.actual_cost), committed = n(tiles.committed);
  const spentPct = budget > 0 ? (spent / budget) * 100 : 0;
  const remaining = Math.max(budget - spent - committed, 0);
  const slices = [
    { name: "Actual cost", v: spent, color: BRAND },
    { name: "Committed", v: committed, color: "#F59E0B" },
    { name: "Remaining", v: remaining, color: "#CBD5E1" },
  ];
  const sliceTotal = slices.reduce((a, s) => a + s.v, 0) || 1;

  const cats = [
    ["Engineering", progress.engineering_actual_pct, "#2563EB"],
    ["Procurement", progress.procurement_actual_pct, "#F59E0B"],
    ["Construction", progress.construction_actual_pct, BRAND],
  ];

  const activity = useMemo(() => {
    const rows = [
      ...latest_daily_reports.map((r) => ({ key: `r${r.id || r.number}`, date: r.report_date, title: `Daily report ${r.number || ""} ${String(r.status || "").toLowerCase() === "draft" ? "drafted" : "submitted"}`, sub: r.status, icon: FileText, bg: "#E0F2FE", fg: "#0284C7", to: `${base}/daily-reports` })),
      ...recent_cost_entries.map((e) => ({ key: `c${e.id}`, date: e.entry_date, title: `Cost booked · ${e.category || "General"}`, sub: `${e.description || "—"} · ${inr(e.amount)}`, icon: Receipt, bg: "#DCFCE7", fg: "#16A34A", to: `${base}/cost-entries` })),
    ];
    return rows.sort((a, b) => String(b.date).localeCompare(String(a.date))).slice(0, 5);
  }, [latest_daily_reports, recent_cost_entries, base]);

  const alerts = [
    ...open_alerts.map((a) => ({ key: `a${a.id || a.title}`, title: String(a.severity || "").toUpperCase() === "CRITICAL" ? "Risk Alert" : "Action Required", body: a.title, date: a.created_at || a.due_date, crit: String(a.severity || "").toUpperCase() === "CRITICAL" })),
    ...procurement_overdue.map((a) => ({ key: `p${a.id || a.title}`, title: "Info", body: a.title, date: a.due_date || a.created_at, info: true })),
  ].slice(0, 4);

  const steps = [
    ...open_issues.slice(0, 2).map((i) => ({ t: `Resolve issue: ${i.description}`, sub: `${i.priority || "Open"} • Issues`, to: `${base}/issues` })),
    ...open_delays.slice(0, 1).map((d) => ({ t: `Address delay: ${d.reason || d.category}`, sub: `${d.impact_days || 0} day impact • Planning`, to: `${base}/delays` })),
    ...procurement_overdue.slice(0, 1).map((a) => ({ t: `Follow up: ${a.title}`, sub: "Overdue • Procurement", to: `${base}/alerts` })),
    ...upcoming_milestones.slice(0, 2).map((m) => ({ t: `Complete milestone: ${m.name}`, sub: `Due ${fmtDate(m.end_date)} • Planning`, to: `${base}/schedule` })),
  ].slice(0, 5);
  const stepColors = [BRAND, "#16A34A", "#EA580C", "#16A34A", "#2563EB"];

  const actions = [
    { l: "Add Daily Report", i: Plus, c: "#16A34A", to: `${base}/daily-reports`, m: projectPageModule("daily-reports") },
    { l: "Create PO", i: ShoppingCart, c: BRAND, to: "/erp/operations/purchase-orders", m: "PURCHASING" },
    { l: "Update Progress", i: BarChart3, c: BRAND, to: `${base}/construction`, m: projectPageModule("construction") },
    { l: "View Gantt Chart", i: GanttChartSquare, c: "#7C3AED", to: `${base}/schedule`, m: projectPageModule("schedule") },
    { l: "Upload Document", i: FileUp, c: "#2563EB", to: `/project-documents?project=${projectId}`, m: null },
    { l: "Raise Issue", i: AlertTriangle, c: "#DC2626", to: `${base}/issues`, m: projectPageModule("issues") },
  ].filter((a) => !a.m || canView(a.m));

  const roles = [
    { id: "pm", t: "Project Manager", i: ClipboardList, c: BRAND, to: base, pts: ["Project overview", "Schedule & progress", "Risks & issues", "Team communication"], match: /manager|lead/i },
    { id: "se", t: "Site Engineer", i: Activity, c: "#16A34A", to: `${base}/daily-reports`, pts: ["Daily reports", "Site inspections", "Task tracking", "Documents"], match: /engineer|site/i },
    { id: "fm", t: "Finance Manager", i: Landmark, c: "#EA580C", to: `${base}/finance`, pts: ["Budget vs actual", "Invoices & payments", "Purchase orders", "Financial reports"], match: /finance|account/i },
    { id: "ct", t: "Contractor", i: Wallet, c: "#DC2626", to: `${base}/documents`, pts: ["Assigned tasks", "Material requests", "Site updates", "Communication"], match: /contract|vendor/i },
  ];
  const myRole = String(projectRole || "");
  const activeRole = roles.find((r) => r.match.test(myRole))?.id;

  const tabs = TABS.filter((t) => t.id === "overview" || canView(projectPageModule(t.suffix)));
  const wanted = sp.get("tab");
  const tab = tabs.some((t) => t.id === wanted) ? wanted : "overview";
  const end = project?.target_end_date || header.target_end_date;
  const start = project?.start_date || header.start_date;
  const loc = project?.location || header.location;
  const name = project?.name || header.name || "Project";

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]" data-testid="project-360-overview">
      <div className="min-w-0 space-y-4">
        {/* hero */}
        <div className="relative overflow-hidden rounded-xl bg-[#0E1A33] text-white shadow-sm" data-testid="project-360-hero">
          <Scene />
          <div className="relative px-6 py-4">
            <nav className="flex items-center gap-2 text-[13px] text-white/80">
              <Link to="/erp/delivery/all-projects" className="hover:text-white">Projects</Link><ChevronRight className="h-3.5 w-3.5" /><span>{name}</span>
            </nav>
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <h1 className="text-[40px] font-semibold leading-none tracking-tight" data-testid="project-360-hero-title">{name}</h1>
              <span className="rounded-full px-4 py-1.5 text-sm font-semibold text-white" style={{ background: health.c }}>{health.t}</span>
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-x-7 gap-y-2 text-[13px] text-white/85">
              {loc && <span className="flex items-center gap-2"><MapPin className="h-4 w-4" />{loc}</span>}
              {header.company_name && <span className="flex items-center gap-2"><Landmark className="h-4 w-4" />{header.company_name}</span>}
              {start && <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />Start: {fmtDate(start)}</span>}
              {end && <span className="flex items-center gap-2"><CalendarDays className="h-4 w-4" />End: {fmtDate(end)}</span>}
            </div>
          </div>
        </div>

        {/* tabs */}
        <nav className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2 shadow-sm" data-testid="project-360-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = t.id === tab;
            return (
              <button key={t.id} type="button" onClick={() => setSp(t.id === "overview" ? {} : { tab: t.id }, { replace: true })} data-testid={`project-360-tab-${t.id}`} aria-pressed={active}
                className={cn("flex items-center gap-2.5 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors", active ? "text-white shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-foreground")}
                style={active ? { background: BRAND } : undefined}>
                <Icon className="h-[18px] w-[18px]" />{t.label}
              </button>
            );
          })}
        </nav>

        {tab !== "overview" ? (
          <TabPanel tab={tab} ctx={{ base, data, project, projectRole, progress, tiles, health, gap, budget, spent, committed, spentPct }} />
        ) : (<>
        {/* row 1 */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card title="Project Progress" link={`${base}/plan-vs-actual`} linkLabel="" testId="project-360-progress-card">
            <div className="flex items-center gap-4">
              <div className="relative h-[150px] w-[150px] shrink-0">
                <PieChart width={150} height={150}>
                  <Pie data={[{ v: overall }, { v: 100 - overall }]} dataKey="v" innerRadius={52} outerRadius={72} startAngle={90} endAngle={-270} stroke="none">
                    <Cell fill={BRAND} /><Cell fill="hsl(var(--muted))" />
                  </Pie>
                </PieChart>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-foreground">{pc(progress.overall_actual_pct)}</span>
                  <span className="text-[10px] text-muted-foreground">Overall Progress</span>
                </div>
              </div>
              <ul className="min-w-0 flex-1 space-y-3 text-[13px]">
                {cats.map(([l, v, c]) => (
                  <li key={l} className="flex items-center gap-2"><i className="h-2 w-2 rounded-full" style={{ background: c }} /><span className="flex-1 truncate text-foreground">{l}</span><b className="text-foreground">{pc(v)}</b></li>
                ))}
              </ul>
            </div>
            <div className="relative mt-4 h-2.5 overflow-hidden rounded-full bg-muted" title={`Planned ${pc(progress.overall_planned_pct)}`}>
              <div className="absolute inset-y-0 left-0 rounded-full bg-[#BFD0F5]" style={{ width: `${planned}%` }} />
              <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${overall}%`, background: BRAND }} />
            </div>
            <div className="mt-3 flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Planned {pc(progress.overall_planned_pct)} · Updated {new Date().toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              <button onClick={onRefresh} className="flex items-center gap-1 font-medium hover:underline" style={{ color: BRAND }} data-testid="project-360-refresh"><RefreshCcw className="h-3 w-3" />Refresh</button>
            </div>
          </Card>

          <Card title="Key Metrics" link={`${base}/finance`} linkLabel="" testId="project-360-metrics-card">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-border p-3"><IconDot icon={CircleDollarSign} bg="#FDE8EC" fg={BRAND} /><p className="mt-2 text-[11px] text-muted-foreground">Total Budget</p><p className="text-lg font-semibold text-foreground" data-testid="metric-total-budget">{inr(budget)}</p></div>
              <div className="rounded-lg border border-border p-3"><IconDot icon={Wallet} bg="#FDE8EC" fg={BRAND} /><p className="mt-2 text-[11px] text-muted-foreground">Spent to Date</p><p className="text-lg font-semibold text-foreground">{inr(spent)}</p>
                <p className="text-[11px] font-semibold" style={{ color: BRAND }}>{spentPct.toFixed(0)}%</p><div className="mt-0.5 h-1 overflow-hidden rounded-full bg-muted"><div className="h-full" style={{ width: `${clamp(spentPct)}%`, background: BRAND }} /></div></div>
              <div className="rounded-lg border border-border p-3"><IconDot icon={TrendingUp} bg="#DCFCE7" fg="#16A34A" /><p className="mt-2 text-[11px] text-muted-foreground">Planned vs Actual</p><p className="text-lg font-semibold" style={{ color: health.c }}>{health.t}</p></div>
              <div className="rounded-lg border border-border p-3"><IconDot icon={CalendarClock} bg="#FDE8EC" fg={BRAND} /><p className="mt-2 text-[11px] text-muted-foreground">Schedule Variance</p><p className={cn("text-lg font-semibold", gap < 0 ? "text-rose-600" : "text-emerald-600")}>{gap > 0 ? "+" : ""}{gap.toFixed(1)} pts</p></div>
            </div>
          </Card>

          <Card title="Quick Actions" testId="project-360-quick-actions">
            <div className="grid grid-cols-2 gap-3">
              {actions.map((a) => (
                <button key={a.l} onClick={() => navigate(a.to)} className="flex flex-col items-center gap-2 rounded-lg border border-border bg-background px-2 py-3 text-center text-[12px] font-medium text-foreground transition-colors hover:bg-accent" data-testid={`quick-action-${a.l.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: `${a.c}1A`, color: a.c }}><a.i className="h-[18px] w-[18px]" /></span>{a.l}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* row 2 */}
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <Card title="Recent Activity" link={`${base}/daily-reports`} testId="project-360-activity-card">
            {activity.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No recent activity</p> : (
              <ul className="space-y-3">
                {activity.map((a) => (
                  <li key={a.key}>
                    <Link to={a.to} className="flex items-start gap-3 hover:opacity-80">
                      <IconDot icon={a.icon} bg={a.bg} fg={a.fg} size="h-8 w-8" />
                      <span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-medium text-foreground">{a.title}</span><span className="block truncate text-[11px] text-muted-foreground">{a.sub}</span></span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">{ago(a.date)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Schedule Overview" link={`${base}/schedule`} linkLabel="View Gantt Chart" testId="project-360-schedule-card">
            {upcoming_milestones.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No upcoming milestones</p> : (
              <ul className="space-y-3.5">
                {upcoming_milestones.slice(0, 5).map((m, i) => {
                  const p = m.progress_pct ?? m.percent_complete ?? m.actual_pct;
                  return (
                    <li key={m.id || i}>
                      <div className="flex items-center justify-between gap-2 text-[12px]"><span className="truncate font-medium text-foreground">{m.name}</span>
                        <span className="shrink-0 text-[11px] text-muted-foreground">{m.start_date ? `${fmtDate(m.start_date)} – ` : "Due "}{fmtDate(m.end_date)}</span></div>
                      {p !== undefined && p !== null && (
                        <div className="mt-1 flex items-center gap-2"><div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full" style={{ width: `${clamp(n(p))}%`, background: n(p) >= 100 ? "#16A34A" : BRAND }} /></div><span className="w-8 text-right text-[11px] text-muted-foreground">{pc(p)}</span></div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>

          <Card title="Financial Summary" link={`${base}/finance`} linkLabel="View Details" testId="project-360-financial-card">
            <div className="relative mx-auto h-[150px] w-[150px]">
              <PieChart width={150} height={150}>
                <Pie data={slices.filter((s) => s.v > 0)} dataKey="v" innerRadius={50} outerRadius={70} stroke="none" paddingAngle={1}>
                  {slices.filter((s) => s.v > 0).map((s) => <Cell key={s.name} fill={s.color} />)}
                </Pie>
              </PieChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center"><span className="text-lg font-bold text-foreground">{inr(spent)}</span><span className="text-[10px] text-muted-foreground">Spent</span></div>
            </div>
            <ul className="mt-3 space-y-1.5 text-[12px]">
              {slices.map((s) => (
                <li key={s.name} className="flex items-center gap-2"><i className="h-2 w-2 rounded-full" style={{ background: s.color }} /><span className="flex-1 text-foreground">{s.name}</span><span className="text-foreground">{inr(s.v)}</span><span className="w-9 text-right text-muted-foreground">{((s.v / sliceTotal) * 100).toFixed(0)}%</span></li>
              ))}
              <li className="flex items-center gap-2 border-t border-border pt-1.5 text-muted-foreground"><span className="flex-1">Forecast at completion</span><span className="text-foreground">{inr(tiles.forecast_at_completion)}</span></li>
              <li className="flex items-center gap-2 text-muted-foreground"><span className="flex-1">Estimate to complete</span><span className="text-foreground">{inr(tiles.estimate_to_complete)}</span></li>
            </ul>
          </Card>
        </div>

        {/* role based view */}
        <section className="rounded-xl border border-border bg-card p-4 shadow-sm" data-testid="project-360-roles">
          <h2 className="mb-3 text-[15px] font-semibold text-foreground">Role-Based View</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {roles.map((r) => {
              const on = activeRole === r.id;
              return (
                <Link key={r.id} to={r.to} className={cn("flex gap-3 rounded-lg border p-3 transition-colors", on ? "border-transparent text-white" : "border-border bg-background hover:bg-accent")} style={on ? { background: BRAND } : undefined}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full" style={{ background: on ? "rgba(255,255,255,.2)" : `${r.c}1A`, color: on ? "#fff" : r.c }}><r.i className="h-4 w-4" /></span>
                  <span className="min-w-0"><span className={cn("block text-[13px] font-semibold", !on && "text-foreground")}>{r.t}</span>
                    <ul className={cn("mt-1 space-y-0.5 text-[11px]", on ? "text-white/85" : "text-muted-foreground")}>{r.pts.map((p) => <li key={p}>• {p}</li>)}</ul></span>
                </Link>
              );
            })}
          </div>
        </section>
        </>)}
      </div>

      {/* right column */}
      <aside className="min-w-0 space-y-4">
        <Card title="Project Alerts" icon={ShieldAlert} link={`${base}/alerts`} testId="project-360-alerts-card">
          {alerts.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">No open alerts</p> : (
            <ul className="divide-y divide-border">
              {alerts.map((a) => (
                <li key={a.key} className="flex items-start gap-3 py-3 first:pt-0">
                  <IconDot icon={a.info ? Info : a.crit ? AlertTriangle : CalendarClock} bg={a.info ? "#DBEAFE" : a.crit ? "#FDE8EC" : "#FEF3C7"} fg={a.info ? "#2563EB" : a.crit ? "#DC2626" : "#D97706"} size="h-10 w-10" />
                  <span className="min-w-0"><span className="block text-[14px] font-semibold" style={{ color: a.info ? "#2563EB" : a.crit ? "#DC2626" : "#D97706" }}>{a.title}</span>
                    <span className="block text-[13px] text-muted-foreground">{a.body}</span>{a.date && <span className="block text-[11px] text-muted-foreground/80">{fmtDate(a.date)}</span>}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Next Steps" icon={ClipboardList} link={`${base}/issues`} testId="project-360-next-steps">
          {steps.length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">You're all caught up.</p> : (
            <ol className="space-y-3.5">
              {steps.map((s, i) => (
                <li key={i}>
                  <Link to={s.to} className="flex items-start gap-3 hover:opacity-80">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: stepColors[i % stepColors.length] }}>{i + 1}</span>
                    <span className="min-w-0"><span className="block truncate text-[13px] font-medium text-foreground">{s.t}</span><span className="block text-[11px] text-muted-foreground">{s.sub}</span></span>
                  </Link>
                </li>
              ))}
            </ol>
          )}
          <div className="mt-4 flex gap-3 rounded-lg bg-sky-50 p-3 dark:bg-sky-950/30">
            <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
            <div className="text-[12px]"><p className="font-semibold text-sky-700 dark:text-sky-300">Pro Tip</p><p className="text-muted-foreground">Keep daily reports updated to get real-time progress and avoid schedule delays.</p>
              <Link to={`${base}/daily-reports`} className="mt-1 inline-block font-medium text-sky-700 hover:underline dark:text-sky-300">Go to Daily Work →</Link></div>
          </div>
        </Card>
      </aside>
    </div>
  );
}
